import React from "react";
import { Box, Chip, Stack, Typography } from "@mui/material";
import { CheckCircle, LockOutlined } from "@mui/icons-material";
import { BillingMode } from "../changeBillingMode.types";
import {
  BILLING_MODE_DESCRIPTIONS,
  BILLING_MODE_LABELS,
} from "../changeBillingMode.constants";

interface ISelectModeStepProps {
  currentMode: BillingMode;
  targetMode: BillingMode;
}

/**
 * Shows the current billing mode (frozen) and the single mode the plan can
 * switch to (pre-selected, the opposite of the current one).
 */
export const SelectModeStep: React.FC<ISelectModeStepProps> = ({
  currentMode,
  targetMode,
}) => {
  return (
    <Stack spacing={2} sx={{ pt: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        Your plan is currently billed as{" "}
        <Typography component="span" fontWeight={700} color="text.primary">
          {BILLING_MODE_LABELS[currentMode]}
        </Typography>
        . You can switch it to{" "}
        <Typography component="span" fontWeight={700} color="text.primary">
          {BILLING_MODE_LABELS[targetMode]}
        </Typography>
        .
      </Typography>

      {/* Current mode — frozen */}
      <Box
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "action.hover",
          px: 2,
          py: 1.5,
          opacity: 0.75,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="body1" fontWeight={700} color="text.primary">
              {BILLING_MODE_LABELS[currentMode]}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {BILLING_MODE_DESCRIPTIONS[currentMode]}
            </Typography>
          </Box>
          <Chip
            size="small"
            icon={<LockOutlined sx={{ fontSize: 14 }} />}
            label="Current"
            sx={{ fontWeight: 700 }}
          />
        </Stack>
      </Box>

      {/* Target mode — selected */}
      <Box
        sx={{
          borderRadius: 2,
          border: "1px solid",
          borderColor: "primary.main",
          bgcolor: "primary.light",
          px: 2,
          py: 1.5,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="body1" fontWeight={700} color="text.primary">
              {BILLING_MODE_LABELS[targetMode]}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {BILLING_MODE_DESCRIPTIONS[targetMode]}
            </Typography>
          </Box>
          <CheckCircle color="primary" />
        </Stack>
      </Box>
    </Stack>
  );
};
